/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { FormControl, InputLabel } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { capitalize, startCase } from "lodash";
import DropdownSearchBar from "../dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../dropdown_infinite_loader";
import { SelectedMenuItem } from "../selected_menu_item";
import { StyledMenuItem } from "../styled_menu_item";
import {
  AddInstanceButtonText,
  StyledAddInstanceButton,
  StyledInstanceContainer,
  StyledInstanceSelect,
  StyledInstanceWrapper,
  StyledResourceSelect,
  StyledRoleSelect,
} from "../../pages/user_management/styles";

export const RESOURCE_TYPES = [
  { name: 'Restaurant Group', value: 'Restaurant_Group' },
  { name: 'Restaurant', value: 'Restaurant' },
];

const INSTANCE_ROLES = [
  { name: 'Admin', value: 'admin' },
  { name: 'Viewer', value: 'viewer' },
];

export const RESOURCE_ROLES = {
  Restaurant_Group: INSTANCE_ROLES,
  Restaurant: INSTANCE_ROLES,
};

const resourceMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  PaperProps: { sx: { maxHeight: "40vh", width: '220px' } },
  autoFocus: false,
};

const roleMenuProps = {
  PaperProps: { sx: { maxHeight: "40vh", width: '220px' } },
  autoFocus: false,
};

const SEARCH_RESET_DELAY = 1000;

/**
 * The instance-level access rows: a resource type, one of its instances, and the role granted on
 * it. Shared by the create and edit modals, which drive identical rows off the same state.
 *
 * @param {Array}    instanceRoles the rows now in the form
 * @param {Object}   handlers      add, remove and the three per-row change handlers
 * @param {Object}   resources     per resource type, the paging refs and search hook its instance
 *                                 dropdown needs, keyed by resource type value
 * @param {Function} getList       options to offer for a resource type
 * @param {Function} getInstanceName label to render for a chosen instance
 */
export const InstanceRoleSection = ({
  instanceRoles = [],
  handlers,
  resources,
  getList,
  getInstanceName,
}) => {
  const {
    onAdd,
    onRemove,
    onResourceTypeChange,
    onResourceInstanceChange,
    onResourceRoleChange,
  } = handlers || {};

  const addButton = (
    <StyledAddInstanceButton onClick={onAdd} data-testid="add-instance-access">
      <AddInstanceButtonText>Add Instance Access</AddInstanceButtonText>
    </StyledAddInstanceButton>
  );

  if (instanceRoles.length === 0) {
    return <div>{addButton}</div>;
  }

  return (
    <React.Fragment>
      {instanceRoles.map((instance) => {
        const resource = resources?.[instance?.resourceType] || resources?.Restaurant;

        return (
          <div key={instance?.id}>
            <StyledInstanceContainer>
              <StyledInstanceWrapper>
                <div style={{height: '40px', width: '100%'}}>
                  <FormControl sx={{ minWidth: 120 }} size="small">
                    <InputLabel style={{color: '#a6a6a6'}}>Resources</InputLabel>
                    <StyledResourceSelect
                      value={instance?.resourceType || ''}
                      renderValue={() => startCase(capitalize(instance?.resourceType))}
                      label="Resource Type"
                      MenuProps={resourceMenuProps}
                      onChange={(e) => onResourceTypeChange?.(instance?.id, e.target.value)}
                    >
                      {RESOURCE_TYPES.map((option) => (
                        <StyledMenuItem key={option.value} value={option.value}>
                          {instance?.resourceType === option.value
                            ? <SelectedMenuItem data={option?.name} />
                            : option?.name}
                        </StyledMenuItem>
                      ))}
                    </StyledResourceSelect>
                  </FormControl>
                </div>

                <div style={{marginLeft: '-5px'}}>:</div>

                <div style={{height: '40px', width: '100%'}}>
                  <FormControl sx={{ minWidth: 120 }} size="small">
                    <InputLabel style={{color: '#a6a6a6'}}>Instances</InputLabel>
                    <StyledInstanceSelect
                      value={instance?.resourceInstance || ''}
                      renderValue={() =>
                        getInstanceName?.(instance?.resourceType, instance?.resourceInstance)
                      }
                      label="Instance"
                      onChange={(e) => onResourceInstanceChange?.(instance?.id, e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          ref: resource?.menuRef,
                          sx: { maxHeight: "40vh", minWidth: '230px' },
                        },
                        autoFocus: false,
                        MenuListProps: { sx: { pt: 0 } },
                      }}
                      onClose={() => setTimeout(() => resource?.onSearchChange?.(''), SEARCH_RESET_DELAY)}
                    >
                      <DropdownSearchBar
                        changeSearchText={(text) => resource?.onSearchChange?.(text)}
                        placeholder={`Search ${`${instance?.resourceType || ''}`.replace('_', ' ').toLowerCase()}`}
                        isFetching={resource?.isFetching}
                      />
                      {instance?.resourceType &&
                        getList?.(instance?.resourceType)?.map((res) => (
                          <StyledMenuItem key={res?.id} value={res?.id}>
                            {`${instance?.resourceInstance}` === `${res?.id}`
                              ? <SelectedMenuItem data={res?.value} />
                              : res?.value}
                          </StyledMenuItem>
                        ))}
                      <DropdownInfiniteLoader
                        hasNextPage={resource?.hasNextPage}
                        targetRef={resource?.loaderRef}
                      />
                    </StyledInstanceSelect>
                  </FormControl>
                </div>

                <div>#</div>

                <div style={{height: '40px', width: '100%'}}>
                  <FormControl sx={{ minWidth: 120 }} size="small">
                    <InputLabel style={{color: '#a6a6a6'}}>Roles</InputLabel>
                    <StyledRoleSelect
                      value={instance?.resourceRole || ''}
                      renderValue={() => startCase(capitalize(instance?.resourceRole))}
                      label="Role"
                      MenuProps={roleMenuProps}
                      onChange={(e) => onResourceRoleChange?.(instance?.id, e.target.value)}
                    >
                      {instance?.resourceType &&
                        RESOURCE_ROLES[instance.resourceType]?.map((option) => (
                          <StyledMenuItem key={option?.value} value={option?.value}>
                            {instance?.resourceRole === option?.value
                              ? <SelectedMenuItem data={option?.name} />
                              : option?.name}
                          </StyledMenuItem>
                        ))}
                    </StyledRoleSelect>
                  </FormControl>
                </div>
              </StyledInstanceWrapper>

              <div>
                <ClearIcon
                  style={{cursor: 'pointer', color: '#1976d2', fontSize: '20px'}}
                  onClick={() => onRemove?.(instance?.id)}
                  data-testid={`remove-instance-access-${instance?.id}`}
                />
              </div>
            </StyledInstanceContainer>
          </div>
        );
      })}
      {addButton}
    </React.Fragment>
  );
};

export default InstanceRoleSection;
